import { useContext, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Card, F0Icon } from '@factorialco/f0-react'
import { Check, Rocket } from '@factorialco/f0-react/icons/app'
import { EnvironmentContext } from 'config/environment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { PricingPlanItemProps } from 'modules/marketplace_modules/components/Revamp/types'
import { getButtonVisibility } from 'modules/marketplace_modules/lib/getButtonVisibility'
import { hasEnterpriseVariant as checkEnterpriseVariant } from 'modules/marketplace_modules/lib/getModuleStatus'
import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'
import UpgradePlanModal from 'modules/upsell/components/SelfUpsell/UpgradePlanModal'
import { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/utils'

export const Item = ({
  addonReference,
  plan,
  onViewAllFeatures,
  onGetItNow,
  onRequestUpsell,
  hasRequestedToday,
  hasRequestedTodayLoading,
  isAuthorized,
  moduleData,
  canSeeSelfUpsell,
  canSeeNormalUpsell,
}: PricingPlanItemProps) => {
  const perksArray = plan.perks ? plan.perks.split('\n').filter(Boolean) : []
  const { ignoreUpsell } = useContext(EnvironmentContext)
  const { company } = useCurrentSession()
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const currentSeats = company.subscription.currentCycleQuantity || company.subscription.seatCount
  const isLargeSegment = currentSeats >= 50
  const isOne = moduleData?.chargebeeModuleName === 'one'

  const legalEntityCountry = company.legalEntities.find((e) => e.main)?.country || ''
  const hasEnterpriseVariant =
    addonReference.length > 0 ? checkEnterpriseVariant(addonReference, legalEntityCountry) : false

  const isInstalled = Boolean(moduleData?.alreadyPurchased)
  const variant = moduleData?.installedVariant ?? null

  const onUpgradePlan = () => {
    setIsUpgradeModalOpen(true)
    track(trackingEvents.marketplaceModules.openUpgradeModal, {
      source: 'marketplace_pricing_card_upgrade_modal',
      module: moduleData?.slug,
      origin: window.location.href,
    })
  }

  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalOpen(false)
  }

  const buttonVisibility = getButtonVisibility({
    isInstalled,
    variant: variant ?? '',
    hasEnterpriseVariant,
    canSeeSelfUpsell: canSeeSelfUpsell || false,
    canSeeNormalUpsell: canSeeNormalUpsell || false,
    ignoreUpsell,
    isLargeSegment,
    hasRequestedToday,
    hasRequestedTodayLoading,
    addonReference,
    moduleData,
  })

  const {
    showSelfUpsellButton,
    shouldShowUpgradeButton,
    shouldShowRequestUpsellButtonPrimary,
    shouldShowRequestUpsellButtonSecondary,
  } = buttonVisibility

  const getPrimaryAction = () => {
    if (!isAuthorized) return undefined
    if (shouldShowInYourPlan) return undefined
    if (isOne && plan.isDowngradeOnePlan) return undefined

    if (shouldShowUpgradeButton) {
      return {
        label: i18n.t('marketplace.revamp.upgrade_plan'),
        onClick: onUpgradePlan || (() => {}),
      }
    }

    if (showSelfUpsellButton) {
      return {
        label: i18n.t('marketplace.revamp.get_it_now'),
        onClick: onGetItNow || (() => {}),
      }
    }

    if (shouldShowRequestUpsellButtonPrimary) {
      return {
        label: i18n.t('upselling_kit.button.request_meeting'),
        onClick: onRequestUpsell || (() => {}),
      }
    }

    if (shouldShowRequestUpsellButtonSecondary) {
      return {
        label: i18n.t('upselling_kit.button.request_meeting'),
        onClick: onRequestUpsell || (() => {}),
      }
    }

    return undefined
  }

  const featuresText = isOne
    ? i18n.t('marketplace.revamp.key_reasons')
    : plan.name === BUSINESS_VARIANT
      ? i18n.t('marketplace.revamp.key_features')
      : plan.name === ENTERPRISE_VARIANT
        ? i18n.t('marketplace.revamp.all_features')
        : i18n.t('marketplace.revamp.key_features')

  const shouldShowInYourPlan = isOne
    ? Boolean(plan.isInstalledOnePlan)
    : isInstalled && plan.name === variant
  return (
    <>
      <Box style={{ height: '100%' }}>
        <F0Card
          avatar={{
            type: 'emoji',
            emoji: plan.icon || '❓',
          }}
          title={plan.name || ''}
          description={canSeeSelfUpsell && !plan.hasDiscount ? plan.price || '' : ''}
          primaryAction={getPrimaryAction()}
          secondaryActions={[
            {
              label: i18n.t('marketplace.revamp.view_all_features'),
              onClick: onViewAllFeatures || (() => {}),
            },
          ]}
        >
          <Box gap="s8" paddingBottom="s8">
            {canSeeSelfUpsell && plan.hasDiscount && plan.originalPrice && (
              <Box gap="s2">
                <Box flexDirection="row" gap="s2">
                  <Text size="200" color="grey700" stroked>
                    {plan.originalPrice}
                  </Text>
                  <Text size="200" color="grey700">
                    {plan.price}
                  </Text>
                </Box>

                <Box flexDirection="row" gap="s2" paddingBottom="s8">
                  {plan.seatOriginalPrice && (
                    <Text size="200" color="grey700" stroked>
                      {plan.seatOriginalPrice}
                    </Text>
                  )}
                  {plan.seatDescription && (
                    <Text size="200" color="grey700">
                      {plan.seatDescription}
                    </Text>
                  )}
                </Box>
              </Box>
            )}

            <Box
              flexDirection="row"
              gap="s8"
              alignItems="flexStart"
              paddingY="s8"
              justifyContent="spaceBetween"
              flexWrap="wrapReverse"
            >
              <Box flexDirection="row" gap="s8" alignItems="flexStart">
                <Box flexDirection="row" gap="s8" alignItems="flexStart" paddingTop="s2">
                  <F0Icon icon={Rocket} size="md" color="default" />
                </Box>
                {}
                <Text size="200" color="black" ellipsis lineClamp={1}>
                  {featuresText}
                </Text>
              </Box>
              {shouldShowInYourPlan && (
                <Box flexDirection="row" gap="s8" alignItems="flexEnd">
                  <F0Icon icon={Check} size="md" color="default" />
                  <Text size="200" color="grey700">
                    {plan.installedOnePlanUntilDate
                      ? i18n.t('self_upsell.first_step.in_plan_until', {
                          defaultValue: 'In your plan until {{date}}',
                          date: plan.installedOnePlanUntilDate,
                        })
                      : i18n.t('marketplace.revamp.in_your_plan')}
                  </Text>
                </Box>
              )}
            </Box>
            <Box gap="s16">
              {perksArray.map((perk, index) => (
                <Box key={index} flexDirection="row" gap="s8" alignItems="flexStart">
                  {perk.trim() ? (
                    <>
                      <F0Icon icon={Check} size="md" color="default" />
                      <Text size="200" color="black" ellipsis lineClamp={1}>
                        {perk}
                      </Text>
                    </>
                  ) : (
                    <Box style={{ height: '20px' }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </F0Card>
      </Box>

      {isValidModule(moduleData?.chargebeeModuleName) && moduleData?.chargebeeModuleName && (
        <UpgradePlanModal
          installedAddonInternalId={moduleData?.installedAddonInternalId ?? null}
          isOpen={isUpgradeModalOpen}
          onClose={handleCloseUpgradeModal}
          moduleName={moduleData.chargebeeModuleName}
          from="marketplace_pricing_card"
        />
      )}
    </>
  )
}
